import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// Detect ?sso_token= in URL and exchange it for a local CPMS session BEFORE
// any other auth check fires. Wipes any prior session first so that one user's
// data never leaks to another. Returns true once the SSO handshake has run
// (success OR failure) so the rest of the app can hydrate.
async function consumeSwamiSSOToken() {
  const params = new URLSearchParams(window.location.search);
  const ssoToken = params.get("sso_token");
  if (!ssoToken) return { handled: false };

  // 1) Wipe any existing local session so no stale data flashes
  try {
    localStorage.removeItem("pms_token");
    localStorage.removeItem("access_token");
    document.cookie = "access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  } catch {
    /* ignore storage errors */
  }

  // 2) Exchange with our backend
  try {
    const { data } = await api.post("/swami-sso/exchange", { token: ssoToken });
    if (data?.token) {
      localStorage.setItem("pms_token", data.token);
    }
    // 3) Strip ?sso_token= from URL without reloading
    params.delete("sso_token");
    const cleanQuery = params.toString();
    const newUrl = window.location.pathname + (cleanQuery ? `?${cleanQuery}` : "") + window.location.hash;
    window.history.replaceState({}, "", newUrl);
    // 4) Pick a landing route based on needs_setup
    const target = data?.needs_setup ? "/setup" : "/";
    if (window.location.pathname !== target) {
      window.history.replaceState({}, "", target);
    }
    return { handled: true, ok: true, user: data?.user || null };
  } catch (err) {
    // Strip token from URL even on failure to avoid retry-loops
    params.delete("sso_token");
    const cleanQuery = params.toString();
    const newUrl = window.location.pathname + (cleanQuery ? `?${cleanQuery}` : "") + window.location.hash;
    window.history.replaceState({}, "", newUrl);
    return { handled: true, ok: false, error: err?.response?.data?.detail || "SSO sign-in failed" };
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Run SSO handshake first — synchronous-ish before any other auth fetches
      const sso = await consumeSwamiSSOToken();
      if (cancelled) return;
      if (sso.handled && !sso.ok) {
        // Surface the failure to the user via the existing toast layer if loaded
        try {
          const { toast } = await import("sonner");
          toast.error(sso.error || "SSO sign-in failed");
        } catch {
          /* toast not available */
        }
      }
      const token = localStorage.getItem("pms_token");
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const r = await api.get("/auth/me");
        if (!cancelled) setUser(r.data);
      } catch (err) {
        // ONLY wipe the local token if the backend explicitly rejected the
        // session (handled by the api.js interceptor, which also redirects to
        // /login for true session failures). For transient errors — network
        // blips, 5xx, cold-starts — keep the token so the next request can
        // succeed without forcing the user back to /login on every refresh.
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const isRealSessionFailure =
          status === 401 &&
          ["Not authenticated", "Token expired", "Invalid token", "User not found"].includes(detail);
        if (isRealSessionFailure) {
          localStorage.removeItem("pms_token");
        }
        // else: leave token in place; user object stays null until next attempt.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("pms_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("pms_token");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
